/**
 * offlineStorage.js
 * ===================
 * Stockage local des Découvertes (estimations) via IndexedDB pour
 * fonctionnement hors-ligne sur le terrain.
 *
 * Stores :
 *   - drafts : Découvertes en attente de sync (tentées localement)
 *   - enrich_cache : Résultats BAN/Cadastre/PLU mis en cache 30 jours
 *
 * Chaque draft a :
 *   - client_uuid : ID local (UUID v4 généré côté client) — sert d'idempotence
 *     pour ne pas créer de doublon si l'agent rejoue la sync
 *   - sync_status : 'pending' | 'syncing' | 'synced' | 'error'
 *   - payload : copie complète du form Découverte
 *   - created_at / updated_at
 *   - last_sync_error : message si échec
 *
 * API exposée :
 *   - listDrafts(), saveDraft(d), deleteDraft(uuid), markSynced(uuid, serverId)
 *   - getCachedEnrichment(key), setCachedEnrichment(key, data)
 */
import { openDB } from 'idb';

const DB_NAME = 'homly_offline_v1';
const DB_VERSION = 1;
const DRAFTS_STORE = 'discovery_drafts';
const ENRICH_STORE = 'enrich_cache';

const ENRICH_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 jours

let _dbPromise = null;

function getDB() {
    if (!_dbPromise) {
        _dbPromise = openDB(DB_NAME, DB_VERSION, {
            upgrade(db) {
                if (!db.objectStoreNames.contains(DRAFTS_STORE)) {
                    const store = db.createObjectStore(DRAFTS_STORE, { keyPath: 'client_uuid' });
                    store.createIndex('sync_status', 'sync_status');
                    store.createIndex('updated_at', 'updated_at');
                }
                if (!db.objectStoreNames.contains(ENRICH_STORE)) {
                    db.createObjectStore(ENRICH_STORE, { keyPath: 'key' });
                }
            },
        });
    }
    return _dbPromise;
}

function genUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    // Fallback safe UUID v4
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

// ─── Discovery Drafts ──────────────────────────────────────────────────────
export async function listDrafts() {
    const db = await getDB();
    const all = await db.getAll(DRAFTS_STORE);
    return all.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
}

export async function listPendingDrafts() {
    const all = await listDrafts();
    return all.filter((d) => d.sync_status !== 'synced');
}

// Backoff exponentiel par tentative (ms) : 30s, 2min, 10min, 1h, 6h (max)
const RETRY_BACKOFFS_MS = [30_000, 120_000, 600_000, 3_600_000, 21_600_000];

export function getBackoffMsForAttempt(attempt) {
    const idx = Math.min(Math.max(attempt - 1, 0), RETRY_BACKOFFS_MS.length - 1);
    return RETRY_BACKOFFS_MS[idx];
}

/**
 * Liste les drafts éligibles à une tentative de sync MAINTENANT :
 *  - tous les "pending"
 *  - les "error" dont next_retry_at est échu (ou absent)
 * Exclut "synced" et "syncing".
 */
export async function listSyncableDrafts() {
    const all = await listDrafts();
    const now = Date.now();
    return all.filter((d) => {
        if (d.sync_status === 'synced') return false;
        if (d.sync_status === 'syncing') return false;
        if (d.sync_status === 'error') {
            if (!d.next_retry_at) return true;
            return Date.parse(d.next_retry_at) <= now;
        }
        return true; // pending
    });
}

export async function getDraft(uuid) {
    const db = await getDB();
    return db.get(DRAFTS_STORE, uuid);
}

export async function saveDraft({ client_uuid, payload }) {
    const db = await getDB();
    const now = new Date().toISOString();
    const existing = client_uuid ? await db.get(DRAFTS_STORE, client_uuid) : null;
    const record = {
        client_uuid: client_uuid || genUUID(),
        payload,
        sync_status: existing?.sync_status === 'synced' ? 'pending' : (existing?.sync_status || 'pending'),
        created_at: existing?.created_at || now,
        updated_at: now,
        last_sync_error: null,
        server_id: existing?.server_id || null,
    };
    await db.put(DRAFTS_STORE, record);
    return record;
}

export async function markSyncing(uuid) {
    const db = await getDB();
    const d = await db.get(DRAFTS_STORE, uuid);
    if (!d) return;
    d.sync_status = 'syncing';
    d.last_sync_error = null;
    await db.put(DRAFTS_STORE, d);
}

export async function markSynced(uuid, serverId) {
    const db = await getDB();
    const d = await db.get(DRAFTS_STORE, uuid);
    if (!d) return;
    d.sync_status = 'synced';
    d.server_id = serverId;
    d.synced_at = new Date().toISOString();
    d.last_sync_error = null;
    d.retry_count = 0;
    d.next_retry_at = null;
    await db.put(DRAFTS_STORE, d);
}

export async function markSyncError(uuid, errMsg) {
    const db = await getDB();
    const d = await db.get(DRAFTS_STORE, uuid);
    if (!d) return;
    const retryCount = (d.retry_count || 0) + 1;
    const backoffMs = getBackoffMsForAttempt(retryCount);
    d.sync_status = 'error';
    d.last_sync_error = String(errMsg).slice(0, 300);
    d.updated_at = new Date().toISOString();
    d.retry_count = retryCount;
    d.next_retry_at = new Date(Date.now() + backoffMs).toISOString();
    await db.put(DRAFTS_STORE, d);
}

/**
 * Force la réinitialisation du retry (passage en 'pending', counter à 0)
 * Utilisé quand l'utilisateur clique manuellement "Réessayer" sur un draft en erreur.
 */
export async function resetRetry(uuid) {
    const db = await getDB();
    const d = await db.get(DRAFTS_STORE, uuid);
    if (!d) return;
    d.sync_status = 'pending';
    d.last_sync_error = null;
    d.retry_count = 0;
    d.next_retry_at = null;
    d.updated_at = new Date().toISOString();
    await db.put(DRAFTS_STORE, d);
}

export async function deleteDraft(uuid) {
    const db = await getDB();
    await db.delete(DRAFTS_STORE, uuid);
}

// Cleanup older synced drafts (older than 7 days)
export async function cleanupOldSyncedDrafts() {
    const db = await getDB();
    const all = await db.getAll(DRAFTS_STORE);
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    let removed = 0;
    for (const d of all) {
        if (d.sync_status === 'synced' && d.synced_at) {
            const t = Date.parse(d.synced_at);
            if (!isNaN(t) && t < cutoff) {
                await db.delete(DRAFTS_STORE, d.client_uuid);
                removed += 1;
            }
        }
    }
    return removed;
}

// ─── Enrich cache (BAN/Cadastre/PLU) ───────────────────────────────────────
function makeEnrichKey({ address, postal_code, city }) {
    return [address || '', postal_code || '', city || ''].join('|').toLowerCase().trim();
}

export async function getCachedEnrichment({ address, postal_code, city }) {
    const db = await getDB();
    const key = makeEnrichKey({ address, postal_code, city });
    const rec = await db.get(ENRICH_STORE, key);
    if (!rec) return null;
    if (rec.expires_at && Date.parse(rec.expires_at) < Date.now()) {
        await db.delete(ENRICH_STORE, key);
        return null;
    }
    return rec.data;
}

export async function setCachedEnrichment({ address, postal_code, city }, data) {
    const db = await getDB();
    const key = makeEnrichKey({ address, postal_code, city });
    await db.put(ENRICH_STORE, {
        key,
        data,
        cached_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + ENRICH_TTL_MS).toISOString(),
    });
}

export async function clearAllOfflineData() {
    const db = await getDB();
    await db.clear(DRAFTS_STORE);
    await db.clear(ENRICH_STORE);
}
